import React from "react";
import axios from "axios";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { LayoutDashboard, Layers3, CalendarRange, GraduationCap, Boxes, FileBarChart2, Settings, Users } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../ui/sonner";
import { getApiBase } from "../../lib/apiBase";

const items = [
  { label: "Accueil", href: "/admin", icon: LayoutDashboard, testId: "admin-nav-home-link" },
  { label: "Utilisateurs Management", href: "/admin/utilisateurs-management", icon: Users, testId: "admin-nav-management-users-link" },
  { label: "BIM 3D + Synthèse", href: "/admin/bim-3d-synthese", icon: Layers3, testId: "admin-nav-bim3d-link" },
  { label: "BIM 4D + Planning", href: "/admin/bim-4d-planning", icon: CalendarRange, testId: "admin-nav-bim4d-link" },
  { label: "Formations", href: "/admin/formations", icon: GraduationCap, testId: "admin-nav-formations-link" },
  { label: "Logiciels", href: "/admin/logiciels", icon: Boxes, testId: "admin-nav-logiciels-link" },
  { label: "Devis / Estimations", href: "/admin/devis-estimations", icon: FileBarChart2, testId: "admin-nav-quotes-link" },
  { label: "Paramètres", href: "/admin/parametres", icon: Settings, testId: "admin-nav-settings-link" },
];

export const AdminLayout = ({ title, description, children }) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    const API = getApiBase();
    await axios.post(`${API}/api/admin-auth/logout`, {}, { withCredentials: true });
    toast.success("Déconnexion réussie.");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(190,242,100,0.12),_transparent_28%),linear-gradient(180deg,_#fbfdf8_0%,_#f8fcf3_44%,_#ffffff_100%)]" data-testid="admin-layout">
      <div className="mx-auto grid max-w-[1600px] gap-6 px-4 py-6 lg:grid-cols-[18rem_1fr] lg:px-6">
        <aside className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 p-5 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.24)]" data-testid="admin-sidebar">
          <div className="flex items-center gap-3 border-b border-[#e8f2ee] pb-5">
            <div className="rounded-2xl border border-[#d8ece4] bg-white p-2 shadow-[0_12px_28px_-20px_rgba(15,118,110,0.22)]">
              <img alt="Bimcubes" className="h-9 w-auto object-contain" data-testid="admin-sidebar-logo" src="/assets/bimcubes-entry-logo.png" />
            </div>
            <div>
              <div className="bim-display text-xl font-semibold text-slate-950" data-testid="admin-sidebar-brand-name">Bimcubes</div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#0f766e]/70" data-testid="admin-sidebar-brand-tagline">Admin</div>
            </div>
          </div>

          <nav className="mt-5 grid gap-2" data-testid="admin-sidebar-nav">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  className={({ isActive }) => `flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition ${isActive ? "bg-[#0f766e] text-white shadow-[0_18px_40px_-28px_rgba(15,118,110,0.45)]" : "text-slate-700 hover:bg-[#ecfdf5] hover:text-[#0f766e]"}`}
                  data-testid={item.testId}
                  key={item.label}
                  to={item.href}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="mt-6 grid gap-3">
            <Button asChild className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]">
              <Link data-testid="admin-sidebar-visitor-link" to="/site">Site visiteur</Link>
            </Button>
            <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-sidebar-logout-button" onClick={handleLogout} variant="outline">
              Se déconnecter
            </Button>
          </div>
        </aside>

        <div className="space-y-6">
          <header className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 px-6 py-5 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-topbar">
            <div className="flex items-center justify-between gap-6">
              <div>
                <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="admin-topbar-eyebrow">Administration</div>
                <h1 className="bim-display mt-2 text-3xl font-semibold tracking-tight text-slate-950" data-testid="admin-topbar-title">{title}</h1>
                <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-600" data-testid="admin-topbar-description">{description}</p>
              </div>
              <div className="hidden rounded-full border border-[#d8ece4] bg-[#f8fcf3] px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0f766e] lg:block" data-testid="admin-topbar-status">
                Accès protégé
              </div>
            </div>
          </header>

          {children}
        </div>
      </div>
      <Toaster closeButton position="top-center" />
    </div>
  );
};